import React, { useState } from "react";
import { TextField, IconButton, Switch, FormControlLabel } from "@mui/material";
import { PlusIcon, SearchIcon, X, FilterIcon } from "lucide-react";
import PosterBox from "./Components/PosterBox";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Permissions } from "../../../permissions";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const Poster = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // "current" or "past"
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [posterName, setPosterName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    // Reset form on close
    setValidFrom(null);
    setValidTo(null);
    setSelectedImage(null);
    setPreviewUrl(null);
    setPosterName("");
    setIsActive(true);
    setOpen(false);
  };

  const createPoster =
    Permissions.find(permission => permission.includes("create_poster")) !==
    undefined;

  const handleImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSavePoster = () => {
    // Validation
    if (!posterName.trim()) {
      alert("Please enter a poster name");
      return;
    }

    if (!validFrom || !validTo) {
      alert("Please select both start and end dates");
      return;
    }

    if (!selectedImage) {
      alert("Please upload an image for the poster");
      return;
    }

    // Simulate submission
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      // Show success message
      alert("Poster created successfully!");
      // Close modal
      handleClose();
    }, 1000);
  };

  return (
    <div className="bg-white h-[calc(100vh-64px)] flex flex-col">
      {/* Header Section with Tabs */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <h1 className="text-2xl font-semibold font-outfit text-[#374151]">
          Posters
        </h1>

        <div className="flex items-center gap-4">
          {/* Tab Buttons */}
          <div className="flex">
            <button
              onClick={() => setActiveTab("current")}
              className={`px-4 py-2 text-sm font-medium transition-all font-outfit ${
                activeTab === "current"
                  ? "text-[#5652B0] border-b-2 border-[#5652B0]"
                  : "text-[#4B5563]"
              }`}>
              Current
            </button>
            <button
              onClick={() => setActiveTab("past")}
              className={`px-4 py-2 text-sm font-medium transition-all font-outfit ${
                activeTab === "past"
                  ? "text-[#5652B0] border-b-2 border-[#5652B0]"
                  : "text-[#4B5563]"
              }`}>
              Past
            </button>
          </div>

          {/* Add Poster Button */}
          {createPoster && (
            <button
              onClick={handleOpen}
              className="flex items-center gap-2 px-5 py-2 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all text-sm font-medium font-outfit">
              <PlusIcon size={18} />
              Add Poster
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex justify-between items-center">
        <div className="relative w-80">
          <SearchIcon
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            size={18}
            color="#83899F"
          />
          <input
            type="text"
            placeholder="Search posters..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-[#E5E7EB] 
              focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
              outline-none transition-all text-[#4B5563] font-outfit"
          />
        </div>

        <button className="flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] text-sm font-outfit text-[#4B5563]">
          <FilterIcon size={16} />
          Filter
        </button>
      </div>

      {/* Posters Grid */}
      <div className="flex-1 p-6 overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 h-full overflow-y-auto">
          {Array(12)
            .fill(null)
            .map((_, index) => (
              <PosterBox key={index} />
            ))}
        </div>
      </div>

      {/* Add Poster Modal */}
      <Modal
        open={open}
        onClose={isSubmitting ? null : handleClose}
        aria-labelledby="add-poster-modal">
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Add New Poster
              </h2>
              <IconButton
                size="small"
                onClick={handleClose}
                disabled={isSubmitting}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            {/* Poster Name */}
            <div className="mb-6">
              <h3 className="text-sm font-medium font-outfit text-[#374151] mb-3">
                Poster Name <span className="text-red-500">*</span>
              </h3>
              <TextField
                label="Enter poster name"
                variant="outlined"
                fullWidth
                size="small"
                value={posterName}
                onChange={e => setPosterName(e.target.value)}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>

            {/* Validity Duration */}
            <div className="mb-6">
              <h3 className="text-sm font-medium font-outfit text-[#374151] mb-3">
                Validity Duration <span className="text-red-500">*</span>
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Valid From"
                    value={validFrom}
                    onChange={newValue => setValidFrom(newValue)}
                    renderInput={params => (
                      <TextField {...params} size="small" />
                    )}
                    className="w-full"
                  />
                  <DatePicker
                    label="Valid To"
                    value={validTo}
                    onChange={newValue => setValidTo(newValue)}
                    renderInput={params => (
                      <TextField {...params} size="small" />
                    )}
                    className="w-full"
                    minDate={validFrom || undefined}
                  />
                </LocalizationProvider>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mb-6">
              <h3 className="text-sm font-medium font-outfit text-[#374151] mb-3">
                Poster Image <span className="text-red-500">*</span>
              </h3>
              {previewUrl ? (
                <div className="mt-2 relative">
                  <img
                    src={previewUrl}
                    alt="Poster preview"
                    className="w-full h-64 object-contain border border-gray-200 rounded-lg"
                  />
                  <button
                    onClick={() => {
                      setSelectedImage(null);
                      setPreviewUrl(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100">
                    <X size={16} className="text-gray-600" />
                  </button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-8 text-center hover:border-[#5652B0] transition-all cursor-pointer"
                  onClick={() =>
                    document.getElementById("poster-image-upload").click()
                  }>
                  <div className="flex flex-col items-center">
                    <img
                      src={require("../../../assets/upload.png")}
                      alt="Upload"
                      className="w-16 h-16 mb-4"
                    />
                    <p className="text-sm font-medium font-outfit text-[#4B5563] mb-1">
                      Drop your image here, or browse
                    </p>
                    <p className="text-xs font-outfit text-[#83899F]">
                      Recommended ratio - 1:1
                    </p>
                    <input
                      type="file"
                      id="poster-image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Poster Status */}
            <div className="mb-6">
              <div className="flex items-center">
                <Switch
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#5652B0",
                    },
                    "&.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#5652B0",
                    },
                  }}
                />
                <div className="ml-2">
                  <p className="text-sm font-medium text-gray-900">
                    {isActive ? "Active" : "Inactive"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {isActive
                      ? "Poster will be visible to customers"
                      : "Poster will be hidden from customers"}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isSubmitting}>
                Cancel
              </button>
              <button
                onClick={handleSavePoster}
                disabled={isSubmitting}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all disabled:bg-opacity-70 disabled:cursor-not-allowed flex items-center gap-2">
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  "Save Poster"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default Poster;
